import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { NOTIFICATION_MESSAGES } from '@/shared/lib/constants';
import type { SynonymEditHook } from '@/shared/types/editHooks';

import type { GlossaryTermDetail } from '@/features/glossary/api';
import { updateGlossaryTermSynonyms } from '@/features/glossary/api/updateGlossaryTermSynonyms';

type UseGlossaryTermSynonymEditParams = {
  termId: string;
  termFqn: string;
};

export const useGlossaryTermSynonymEdit = ({ termId, termFqn }: UseGlossaryTermSynonymEditParams): SynonymEditHook => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [synonyms, setSynonyms] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');

  const queryKey = useMemo(() => ['glossaryTermDetail', termFqn] as const, [termFqn]);

  const termData = queryClient.getQueryData<GlossaryTermDetail>(queryKey);
  const currentSynonyms = useMemo(() => termData?.synonyms || [], [termData?.synonyms]);

  useEffect(() => {
    setSynonyms(currentSynonyms);
  }, [currentSynonyms]);

  const updateSynonymsMutation = useMutation({
    mutationFn: async (newSynonyms: string[]) => {
      await updateGlossaryTermSynonyms(termId, newSynonyms, currentSynonyms);
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey });
      notification.error({
        message: NOTIFICATION_MESSAGES.SYNONYM_UPDATE_ERROR || '동의어 업데이트 실패',
        description: error instanceof Error ? error.message : '동의어 업데이트에 실패했습니다.',
        duration: 5,
        placement: 'bottomLeft',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      notification.success({
        message: NOTIFICATION_MESSAGES.SYNONYM_UPDATE_SUCCESS || '동의어 업데이트 성공',
        description: NOTIFICATION_MESSAGES.SYNONYM_UPDATE_SUCCESS_DESC || '동의어가 성공적으로 업데이트되었습니다.',
        placement: 'bottomLeft',
        duration: 5,
      });
    },
  });

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    const latestTermData = queryClient.getQueryData<GlossaryTermDetail>(queryKey);
    const latestSynonyms = latestTermData?.synonyms || [];
    setSynonyms(latestSynonyms);
    setInputValue('');
  }, [queryClient, queryKey]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    const latestTermData = queryClient.getQueryData<GlossaryTermDetail>(queryKey);
    const latestSynonyms = latestTermData?.synonyms || [];
    setSynonyms(latestSynonyms);
    setInputValue('');
  }, [queryClient, queryKey]);

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
  }, []);

  const handleAddSynonym = useCallback(() => {
    const trimmedValue = inputValue.trim();

    if (!trimmedValue) {
      return;
    }

    if (synonyms.includes(trimmedValue)) {
      notification.warning({
        message: '중복된 동의어',
        description: '이미 추가된 동의어입니다.',
        duration: 5,
        placement: 'bottomLeft',
      });
      return;
    }

    setSynonyms((prev) => [...prev, trimmedValue]);
    setInputValue('');
  }, [inputValue, synonyms]);

  const handleRemoveSynonym = useCallback((synonym: string) => {
    setSynonyms((prev) => prev.filter((s) => s !== synonym));
  }, []);

  const handleSynonymsChange = useCallback((newSynonyms: string[]) => {
    setSynonyms(newSynonyms);
  }, []);

  const handleUpdate = useCallback(() => {
    updateSynonymsMutation.mutate(synonyms, {
      onSuccess: () => {
        setIsOpen(false);
      },
    });
  }, [synonyms, updateSynonymsMutation]);

  const hasChanges = useMemo(() => {
    if (synonyms.length !== currentSynonyms.length) return true;

    const sortedSynonyms = [...synonyms].sort();
    const sortedCurrent = [...currentSynonyms].sort();

    return sortedSynonyms.some((val, idx) => val !== sortedCurrent[idx]);
  }, [synonyms, currentSynonyms]);

  return {
    isOpen,
    synonyms,
    inputValue,
    isUpdating: updateSynonymsMutation.isPending,
    hasChanges,
    handleOpen,
    handleClose,
    handleInputChange,
    handleAddSynonym,
    handleRemoveSynonym,
    handleSynonymsChange,
    handleUpdate,
  };
};
